import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, getCategoryById } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return <NotFound />;
  }

  const category = getCategoryById(article.category);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '夜勤病栋解析站',
    },
    inLanguage: 'zh-CN',
    about: article.tags.map((tag) => ({ '@type': 'Thing', name: tag })),
    keywords: article.tags.join(','),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <main className="pt-20 pb-16 bg-background-50">
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                ...(category
                  ? [{ label: category.name, href: `/category/${article.category}` }]
                  : []),
                { label: article.title },
              ]}
            />
          </div>

          <div className="mb-6">
            {category && (
              <Link
                to={`/category/${article.category}`}
                className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-500/10 text-primary-400 hover:bg-primary-500/20 transition-colors mb-4 whitespace-nowrap"
              >
                {category.name}
              </Link>
            )}
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-100 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readTime} 分钟阅读
              </span>
            </div>
          </div>

          <div className="mb-8 rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full object-cover"
              title={`${article.title} - 夜勤病栋深度解析`}
            />
          </div>

          <div
            className="prose-custom"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-8 pt-6 border-t border-background-200/60">
            <div className="flex flex-wrap gap-2 mb-6">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-400 border border-background-200/60 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <Link
                to={category ? `/category/${article.category}` : '/'}
                className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-400 transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                {category ? `返回${category.name}` : '返回首页'}
              </Link>
              <div className="flex items-center gap-3">
                <span className="text-xs text-foreground-600">分享到：</span>
                <a href="#" className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:text-primary-400 hover:bg-primary-500/10 transition-colors" aria-label="微博分享">
                  <i className="ri-weibo-line"></i>
                </a>
                <a href="#" className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:text-primary-400 hover:bg-primary-500/10 transition-colors" aria-label="微信分享">
                  <i className="ri-wechat-line"></i>
                </a>
                <a href="#" className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:text-primary-400 hover:bg-primary-500/10 transition-colors" aria-label="复制链接">
                  <i className="ri-link"></i>
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>
    </>
  );
}